package com.solevo.dist.di

// repositories intentionally not bound; pages use data sources directly for web
import com.solevo.dist.AppConfig
import com.solevo.dist.application.entities.countries.service.CountryRemoteService
import com.solevo.dist.network.api.orders.OrderRemoteSource
import com.solevo.dist.network.api.invoice.InvoiceRemoteSource
import com.solevo.dist.network.api.products.ProductRemoteSource
import com.solevo.dist.network.api.transactions.TransactionRemoteSource
import com.solevo.dist.network.api.user.UserRemoteSource
import com.solevo.dist.network.firebase.api.orders.OrderFirebaseRemoteSource
import com.solevo.dist.network.firebase.api.user.UserFirebaseRemoteSource
import com.solevo.dist.network.firebase.api.invoice.InvoiceFirestoreRemoteSource
import com.solevo.dist.network.api.quotes.QuoteRemoteSource
import com.solevo.dist.network.implementation.http.api.quotes.QuoteHttpRemoteSource
import com.solevo.dist.utils.di.QuoteHttpQualifier
import com.solevo.dist.utils.di.IODispatcher
import com.solevo.dist.utils.di.BaseUrl
import com.solevo.dist.network.firebase.api.quotes.QuoteFirebaseRemoteSource
import com.solevo.dist.repository.ApplicationQuoteRepository
import com.solevo.dist.repository.ApplicationCustomerRepository
import com.solevo.dist.repository.ApplicationStatementRepository
import com.solevo.dist.repository.implementation.quotes.QuoteRepository
import com.solevo.dist.repository.implementation.customer.CustomerRepository
import com.solevo.dist.repository.implementation.statements.StatementRepository
import com.solevo.dist.utils.di.QuoteFirebaseQualifier
import com.solevo.dist.network.firebase.api.transactions.TransactionFirestoreRemoteSource
import com.solevo.dist.network.firebase.api.products.ProductFirestoreRemoteSource
import com.solevo.dist.network.firebase.api.notifications.NotificationFirebaseRemoteSource
import com.solevo.dist.network.api.notifications.NotificationRemoteSource
import com.solevo.dist.repository.ApplicationNotificationRepository
import com.solevo.dist.repository.implementation.notification.NotificationRepository
import com.solevo.libraries.authentication.integrations.graph.appCheckDebugTokenQualifier
import com.solevo.libraries.authentication.integrations.graph.authentication
import com.solevo.libraries.authentication.integrations.graph.reCaptchaSiteKeyQualifier
import com.solevo.libraries.authentication.integrations.ktor.services.phoneNumber.ktorPhoneNumberAuthenticator
import com.solevo.libraries.device.files.Files
import com.solevo.libraries.device.files.WebFiles
import com.solevo.libraries.entities.application.environment.ApplicationEnvironment
import com.solevo.libraries.entities.application.version.ApplicationVersion
import com.solevo.libraries.entities.country.RemoteCountryService
import com.solevo.libraries.entities.integrations.graph.entities
import com.solevo.libraries.entities.integrations.logging.KoinLogBuilding
import com.solevo.libraries.entities.language.LanguageCenter
import com.solevo.libraries.entities.logging.LoggingCenter
import com.solevo.libraries.entities.logging.LogTypeBuilding
import com.solevo.libraries.entities.phone.PhoneNumber
import com.solevo.libraries.entities.phone.PhoneNumberValidator
import com.solevo.libraries.entities.platform.Platform
import com.solevo.libraries.entities.platform.Platform.OS
import com.solevo.libraries.feature.authentication.services.AuthenticationServices
import com.solevo.libraries.feature.authentication.services.phoneNumber.PhoneNumberAuthenticationServices
import com.solevo.libraries.network.integrations.ktor.KtorNetworkClient
import com.solevo.libraries.network.infrastructure.NetworkClient
import com.solevo.libraries.network.integrations.ktor.AdditionalHttpClientConfiguration
import com.solevo.libraries.network.integrations.ktor.ktorClient
import com.solevo.libraries.entities.application.session.ApplicationSession
import io.ktor.client.plugins.api.createClientPlugin
import io.ktor.client.request.header
import dev.gitlive.firebase.Firebase
import dev.gitlive.firebase.auth.auth
import dev.gitlive.firebase.firestore.firestore
import org.koin.dsl.module

/**
 * Created by Ilija Vucetic on 3.9.25..
 * Copyright (c) 2025 Aktiia. All rights reserved.
 */

import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.serialization.json.Json
import org.koin.core.qualifier.StringQualifier
import org.koin.dsl.bind

val ktorBaseUrl = StringQualifier("ktorBaseUrl")
val appPath = StringQualifier("appApiPath")

// Deliberately not named `appModule` — that is already the Koin module below.
val appModuleName = StringQualifier("appModule")

class EmptyPhoneNumberRegistrationHub :
    PhoneNumberAuthenticationServices.PhoneNumberRegistrationHub {
    override suspend fun isRegistered(phoneNumber: PhoneNumber): Result<Boolean> {
        return Result.success(true)
    }

    override suspend fun reportPhoneNumberRegistration(phoneNumber: PhoneNumber): Result<Unit> {
        return Result.success(Unit)
    }
}

class EmptyAuthenticationConfiguration : AuthenticationServices.Configuration {
    override suspend fun shouldCompleteLoginWithTokenOnly(): Result<Boolean> {
        return Result.success(false)
    }

    override suspend fun shouldCompleteRegisterWithTokenOnly(): Result<Boolean> {
        return Result.success(false)
    }
}



val appModule = module {
    single {
        object : PhoneNumberValidator {
            override suspend fun validate(phoneNumber: PhoneNumber): Result<Unit> {
                return Result.success(Unit)
            }
        }
    } bind PhoneNumberValidator::class

    // Logging
    single {
        LoggingCenter(
            KoinLogBuilding(emptyMap(), emptyMap()),
            mapOf()
        )
    }

    // Services
    single { CountryRemoteService() } bind RemoteCountryService::class
    single<Files> { WebFiles() }

    // JSON
    single<Json> {
        Json {
            ignoreUnknownKeys = true
            isLenient = true
            encodeDefaults = true
            prettyPrint = false
        }
    }

    single {
        LanguageCenter()
    }

    // Network client
    single {
        val applicationSession: ApplicationSession = get()
        // The backend ties the subsystem secret it issues to the application session, so every
        // request has to carry the id. It is read from a suspending source, which `defaultRequest`
        // cannot do, so it is set from `onRequest` instead.
        val sessionHeaders = createClientPlugin("ApplicationSessionHeaders") {
            onRequest { request, _ ->
                request.header("X-Application-Session-Id", applicationSession.getSessionId())
            }
        }
        val ktor = ktorClient(
            languageCenter = get(),
            ApplicationEnvironment(
                isDebugBuild = true,
                environment = ApplicationEnvironment.Environment.Test
            ),
            ApplicationVersion("0.0.1", "1", kotlinx.datetime.Clock.System.now()),
            platform = Platform.Web(OS.Web("1.0.0")),
            additionalHttpClientConfiguration = AdditionalHttpClientConfiguration {
                install(sessionHeaders)
            }
        )
        KtorNetworkClient(ktor, get())
    } bind NetworkClient::class

    // Entities
    entities()

    // Config values
    single(ktorBaseUrl) { AppConfig.BACKEND_BASE_URL + "/" }
//    single(ktorBaseUrl) { kotlinx.browser.window.location.origin + "/" }
    single(appPath) { "" }
    single(appModuleName) { "Distributor" }
    // App Check (reCAPTCHA Enterprise). The debug token is only ever present in dev/test configs.
    single(reCaptchaSiteKeyQualifier) { AppConfig.Firebase.RECAPTCHA_SITE_KEY }
    AppConfig.Firebase.APP_CHECK_DEBUG_TOKEN?.let { token -> single(appCheckDebugTokenQualifier) { token } }
    // Qualifier aliases used by shared modules
    single(IODispatcher) { Dispatchers.Default }
    single(BaseUrl) { get<String>(ktorBaseUrl) }

    // Coroutine scope
    single { CoroutineScope(Dispatchers.Default) }

    // Auth services
    single { EmptyPhoneNumberRegistrationHub() } bind PhoneNumberAuthenticationServices.PhoneNumberRegistrationHub::class
    single { EmptyAuthenticationConfiguration() } bind AuthenticationServices.Configuration::class

    authentication(
        baseUrlQualifier = ktorBaseUrl,
        appApiPath = appPath,
        appModuleQualifier = appModuleName,
        onLogout = { },
        optionalBinding = { },
        phoneNumberAuthenticatorQualifier = ktorPhoneNumberAuthenticator,
    )

    // Firebase (dev.gitlive)
    single { Firebase.firestore }
    single { Firebase.auth }
    single { OrderFirebaseRemoteSource(get(), get()) } bind OrderRemoteSource::class
    // Firebase live quotes for UI
    single(QuoteFirebaseQualifier) { QuoteFirebaseRemoteSource() } bind QuoteRemoteSource::class
    // HTTP quotes for actions that require backend + access token
    single(QuoteHttpQualifier) { QuoteHttpRemoteSource(get<kotlinx.coroutines.CoroutineDispatcher>(IODispatcher), get<String>(BaseUrl), get<com.solevo.libraries.network.infrastructure.NetworkClient>(), get<kotlinx.serialization.json.Json>()) } bind QuoteRemoteSource::class

    // Repository used by web to call HTTP cancelQuote with AccessTokenRemoteService
    single { QuoteRepository(get(), get(QuoteFirebaseQualifier), get(QuoteHttpQualifier), get()) } bind ApplicationQuoteRepository::class
    // Bind repositories for statements page
    single { CustomerRepository(get()) } bind ApplicationCustomerRepository::class
    single { StatementRepository(get(), get(), get()) } bind ApplicationStatementRepository::class
    single { InvoiceFirestoreRemoteSource() } bind InvoiceRemoteSource::class
    single { TransactionFirestoreRemoteSource() } bind TransactionRemoteSource::class
    single { ProductFirestoreRemoteSource() } bind ProductRemoteSource::class
    single { UserFirebaseRemoteSource() } bind UserRemoteSource::class
    single { com.solevo.dist.network.firebase.CustomerFirebaseRemoteSource() } bind com.solevo.dist.network.api.customers.CustomerRemoteSource::class
    // Notifications
    single { NotificationFirebaseRemoteSource() } bind NotificationRemoteSource::class
    single { NotificationRepository(get()) } bind ApplicationNotificationRepository::class
}